import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import VideoBox from "../../casinoComponents/videoBox";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import UserSiteLastResult from "../../casinoComponents/userSiteLastResult";
import VirtualVideo from "../../casinoComponents/userSiteLastResult/VirtualVideo";
import VirtualRightSide from "../../casinoComponents/userSiteLastResult/VirtualRightSide";
import { RefCard } from "../../casinoComponents/userSiteLastResult/refBits";
import { joinSlots } from "../../casinoComponents/userSiteLastResult/refUtils";
import RefCardsOverlay from "../../casinoComponents/videoBox/overlays/RefCardsOverlay";
import BtableResultBody from "../../casinoComponents/refResultModal/BtableResultBody";
import ViewMoreModal from "../../../components/ViewMoreModal";
import {
  getCasinoGameDetails,
  getMatchExposure,
  getCasinoOpenBets,
} from "../../../apiservices/CasionApi";
import { extractCasinoGame, isVirtualCasinoType } from "../../../utils/casinoGame";
import BtableLiveTable from "./BtableLiveTable";
import BtableVirtualTable from "./BtableVirtualTable";
import "../../../styles/games/btabledtl.css";

// Reference titles (lastresults2 res1.cname)
const TITLES = { btable: "Bollywood Casino", btable2: "Bollywood Casino 2", vbtable: "V-Bollywood Casino" };

// Reference LastResultsSection (btable / btable2 / vbtable): win 1..6 = A..F, 0 = tie
const btableItem = (win) => ({
  className: `resultb${win === "0" ? " resulttie" : ""}`,
  content: joinSlots(win, [[1, "A"], [2, "B"], [3, "C"], [4, "D"], [5, "E"], [6, "F"], [0, "T"]]),
});

// Bollywood Casino — Live Market (/casino/btable) aur Live Virtual Market
// (/vcasino/btable → vbtable). Admin me ye page pehle tha hi nahi (menu link
// wapis Market Analysis par bhej deta tha).
export default function Btable() {
  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "btable";
  const [gameData, setGameData] = useState({});
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const raw = gameData?.data?.data || {};
  const matchId = raw?.mid ? String(raw.mid) : "";

  useEffect(() => {
    let cancelled = false;
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        if (!cancelled && response) setGameData(response);
      } catch (err) {
        console.error("Error fetching Bollywood game data:", err);
      }
    };
    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [gameName]);

  const fetchExposure = useCallback(async () => {
    if (!matchId) return;
    try {
      const response = await getMatchExposure(null, matchId);
      if (response?.success && Array.isArray(response.data)) {
        const map = {};
        response.data.forEach((item) => {
          const name = String(item.team_name || "").trim();
          const amount = parseFloat(item.exposure_amount) || 0;
          if (name && amount !== 0) {
            map[name] = (map[name] || 0) + amount;
            map[name.toLowerCase()] = map[name];
          }
        });
        setExposures(map);
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        setMyBets(
          response.data.map((bet) => ({
            // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.username || "-",
            matchedBet: bet.selection || bet.player_name || "-",
            selection: bet.selection || "",
            odds: bet.odds ?? "0",
            stake: bet.amount ?? "0",
            placeDate: bet.created_at ?? "-",
            gametype: bet.game_type === "lay" ? "Lay" : "Back",
            type: bet.game_type || null,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [gameName]);

  useEffect(() => {
    if (!matchId) return undefined;
    fetchExposure();
    const id = setInterval(fetchExposure, 2000);
    return () => clearInterval(id);
  }, [matchId, fetchExposure]);

  useEffect(() => {
    fetchMyBetsData();
    const id = setInterval(fetchMyBetsData, 3000);
    return () => clearInterval(id);
  }, [fetchMyBetsData]);

  const isVirtual = isVirtualCasinoType(gameName);
  const title = TITLES[gameName] || TITLES.btable;
  const roundId = raw?.mid ?? "";
  const viewMoreModal = (
    <ViewMoreModal
      show={showViewMoreModal}
      onHide={() => setShowViewMoreModal(false)}
      title="View More"
      sectionKey={gameName}
    />
  );
  const lastResults = (
    <UserSiteLastResult gameType={gameName} renderItem={btableItem} Body={BtableResultBody} fallbackTitle={title} />
  );

  // Live Virtual Market (/vcasino/btable): reference .casino-table.vcasino.vbt
  if (isVirtual) {
    const cards = String(raw?.card ?? "").split(",");
    return (
      <CasinoTableLayout
        tableClass="vcasino vbt"
        rightSide={<VirtualRightSide bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
      >
        <>
          <VirtualVideo
            gameType={gameName}
            title={title}
            data={raw}
            cardsNode={
              <div className="vcasino-cards">
                <div className="text-center w-100">
                  <RefCard card={cards[0]} />
                </div>
              </div>
            }
          />{" "}
          {roundId ? (
            <div className="casino-detail">
              <BtableVirtualTable raw={raw} exposures={exposures} gameType={gameName} />{" "}
              <div className="casino-video-last-results">{lastResults}</div>
            </div>
          ) : null}
        </>
        {viewMoreModal}
      </CasinoTableLayout>
    );
  }

  // Live Market (/casino/btable, /casino/btable2): reference .casino-table.aaa
  // (data-btl only scopes this cluster's CSS fixes away from the AAA pages that share .aaa)
  return (
    <CasinoTableLayout
      tableClass="aaa"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading name={title} roundId={roundId} />
        <VideoBox
          gameType={gameName}
          iframeSrc={`https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`}
          timerValue={raw?.lt}
          cardsOverlay={<RefCardsOverlay card={raw?.card} count={1} wrap />}
        />
        {roundId ? (
          <div className="casino-detail" data-btl="">
            <BtableLiveTable raw={raw} exposures={exposures} />{" "}
            <CasinoLastResult
              gameType={gameName}
              viewAllLink={`/admin/reports/casinoresult/${gameName}`}
              resultComponent={lastResults}
            />
          </div>
        ) : null}
      </>
      {viewMoreModal}
    </CasinoTableLayout>
  );
}
